import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import {
  DashboardOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  BankOutlined,
  GiftOutlined,
  UsergroupAddOutlined,
  DatabaseOutlined,
  CustomerServiceOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  ProfileOutlined,
  ScheduleOutlined,
  ExceptionOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  QuestionCircleOutlined,
  PictureOutlined,
  OrderedListOutlined,
  BarChartOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAppSelector } from '@/store/hooks';
import DocditLogo from '@/assets/logo/brand_site.svg';

const { Sider } = Layout;
type MenuItem = Required<MenuProps>['items'][number];

const getMenuItem = (
  label: React.ReactNode,
  key: string,
  icon?: React.ReactNode,
  children?: MenuItem[]
): MenuItem => ({
  key,
  icon,
  children,
  label,
});

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarCollapsed } = useAppSelector((state) => state.ui);

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const menuItems: MenuItem[] = useMemo(
    () => [
      getMenuItem('Dashboard', '/dashboard', <DashboardOutlined />),

      getMenuItem('Administrators', 'administrators', <TeamOutlined />, [
        getMenuItem('My Account', '/administrators/profile', <UserOutlined />),
        getMenuItem('Sub Admins', '/administrators/list', <TeamOutlined />),
        getMenuItem(
          'Roles & Permissions',
          '/administrators/roles',
          <SafetyCertificateOutlined />
        ),
      ]),

      getMenuItem('Users', 'users', <UserOutlined />, [
        getMenuItem('All Users', '/users', <OrderedListOutlined />),
        getMenuItem('Cashback List', '/users/cashbacks', <DollarOutlined />),
        getMenuItem('Inactive Users', '/users/inactive', <ClockCircleOutlined />),
        getMenuItem('Deleted Users', '/users/deleted', <CloseCircleOutlined />),
      ]),

      getMenuItem('Doctors', 'doctors', <MedicineBoxOutlined />, [
        getMenuItem('All Doctors', '/doctors', <OrderedListOutlined />),
        getMenuItem('Master Data', 'doctors-master', <DatabaseOutlined />, [
          getMenuItem('Specialities', '/doctors/master/specialities'),
          getMenuItem('Treatments', '/doctors/master/treatments'),
          getMenuItem('Qualifications', '/doctors/master/qualifications'),
          getMenuItem('Schools', '/doctors/master/schools'),
          getMenuItem('Templates', '/doctors/master/templates'),
          getMenuItem('CPT/EM Codes', '/doctors/master/cpt-codes'),
          getMenuItem('Day Times', '/doctors/master/day-times'),
          getMenuItem('Durations', '/doctors/master/durations'),
          getMenuItem('Units', '/doctors/master/units'),
        ]),
        getMenuItem('Signed Up', '/doctors?status=pending', <ClockCircleOutlined />),
        getMenuItem('Awaiting', '/doctors?status=awaiting', <ExceptionOutlined />),
        getMenuItem('Verified', '/doctors?status=verified', <CheckCircleOutlined />),
        getMenuItem('Rejected', '/doctors?status=rejected', <CloseCircleOutlined />),
        getMenuItem('Unmatched', '/doctors?status=unmatched', <WarningOutlined />),
      ]),

      getMenuItem('Appointments', 'appointments', <CalendarOutlined />, [
        getMenuItem('All Appointments', '/appointments', <OrderedListOutlined />),
        getMenuItem('Waiting Room', '/appointments/waiting-room', <ClockCircleOutlined />),
        getMenuItem('Upcoming', '/appointments/upcoming', <ScheduleOutlined />),
        getMenuItem('Past', '/appointments/past', <ProfileOutlined />),
        getMenuItem('Completed', '/appointments/completed', <CheckCircleOutlined />),
        getMenuItem('Cancelled', '/appointments/cancelled', <CloseCircleOutlined />),
        getMenuItem('Notes', 'notes', <FileTextOutlined />, [
          getMenuItem('Unfinished Notes', '/appointments/notes/unfinished', <ExceptionOutlined />),
          getMenuItem('Finished Notes', '/appointments/notes/finished', <CheckCircleOutlined />),
        ]),
        // getMenuItem('Claims', 'claims', <AuditOutlined />, [
        //   getMenuItem('Claims List', '/appointments/claims/list'),
        //   getMenuItem('Claims Groups', '/appointments/claims/groups'),
        //   getMenuItem('Manual Payment', '/appointments/claims/manual-payment'),
        // ]),
      ]),

      getMenuItem('Prescriptions', '/prescriptions', <FileTextOutlined />),

      getMenuItem('Clinics', 'clinics', <BankOutlined />, [
        getMenuItem('Add New Clinic', '/clinics/create', <BankOutlined />),
        getMenuItem('All Clinics', '/clinics/list', <OrderedListOutlined />),
        getMenuItem('Clinic Doctors', '/clinics/doctors', <MedicineBoxOutlined />),
        getMenuItem('Appointments', 'clinic-appointments', <CalendarOutlined />, [
          getMenuItem('All', '/clinics/appointments'),
          getMenuItem('Waiting Room', '/clinics/appointments?status=waiting_room', <ClockCircleOutlined />),
          getMenuItem('Upcoming', '/clinics/appointments?status=upcoming', <ScheduleOutlined />),
          getMenuItem('Completed', '/clinics/appointments?status=completed', <CheckCircleOutlined />),
          getMenuItem('Cancelled', '/clinics/appointments?status=cancelled', <CloseCircleOutlined />),
        ]),
      ]),

      getMenuItem('Billing', 'billing', <DollarOutlined />, [
        getMenuItem('Dashboard', '/billing/dashboard', <DashboardOutlined />),
        getMenuItem('All Invoices', '/billing/invoices', <FileTextOutlined />),
        getMenuItem('Subscription Plans', '/billing/plans', <SafetyCertificateOutlined />),
        getMenuItem('Promo Codes', '/billing/promo-codes', <GiftOutlined />),
      ]),

      // getMenuItem('Coupons', 'coupons', <GiftOutlined />, [
      //   getMenuItem('Coupon List', '/coupons/list', <OrderedListOutlined />),
      //   getMenuItem('Coupon History', '/coupons/history', <ProfileOutlined />),
      // ]),

      getMenuItem('Agents', 'agents', <UsergroupAddOutlined />, [
        getMenuItem('All Agents', '/agents', <OrderedListOutlined />),
        getMenuItem('Payouts', '/agents/payouts', <DollarOutlined />),
      ]),

      // getMenuItem('Fast Track Consult', 'ftc', <ThunderboltOutlined />, [
      //   getMenuItem('FTC List', '/ftc/list', <OrderedListOutlined />),
      //   getMenuItem('FTC Settings', '/ftc/settings', <SettingOutlined />),
      // ]),

      getMenuItem('Notifications', 'notifications', <MailOutlined />, [
        getMenuItem('All Notifications', '/notifications', <OrderedListOutlined />),
        getMenuItem('Send New', '/notifications/send', <MailOutlined />),
        getMenuItem('Templates', '/notifications/templates', <FileTextOutlined />),
        getMenuItem('Statistics', '/notifications/stats', <BarChartOutlined />),
      ]),

      getMenuItem('Master Data', 'master-data', <DatabaseOutlined />, [
        getMenuItem('Email Templates', '/master-data/email-templates', <MailOutlined />),
        getMenuItem('SMS Templates', '/master-data/sms-templates', <PhoneOutlined />),
        getMenuItem('Pages', '/master-data/pages', <FileTextOutlined />),
        getMenuItem('Blogs', '/master-data/blogs', <ProfileOutlined />),
        getMenuItem('States', '/master-data/states', <GlobalOutlined />),
        getMenuItem('Banners', '/master-data/banners', <PictureOutlined />),
        // getMenuItem('Blocked List', '/master-data/blocked', <BlockOutlined />),
        // getMenuItem(
        //   'Insurance Providers',
        //   '/master-data/insurance-providers',
        //   <SafetyCertificateOutlined />
        // ),
        // getMenuItem('Conditions', '/master-data/conditions', <TagsOutlined />),
      ]),

      getMenuItem('Support', 'support', <CustomerServiceOutlined />, [
        getMenuItem('Tickets', '/support/tickets', <OrderedListOutlined />),
        getMenuItem('FAQ', '/support/faq', <QuestionCircleOutlined />),
      ]),

      getMenuItem('Reports', 'reports', <BarChartOutlined />, [
        getMenuItem('Overview', '/reports', <PieChartOutlined />),
        getMenuItem('Appointments', '/reports/appointments', <CalendarOutlined />),
        getMenuItem('Revenue', '/reports/revenue', <DollarOutlined />),
        getMenuItem('Doctors', '/reports/doctors', <MedicineBoxOutlined />),
        getMenuItem('Users', '/reports/users', <UserOutlined />),
        getMenuItem('Prescriptions', '/reports/prescriptions', <FileTextOutlined />),
      ]),

      getMenuItem('Settings', 'settings', <SettingOutlined />, [
        getMenuItem('General', '/settings/general', <GlobalOutlined />),
        getMenuItem('Payment', '/settings/payment', <DollarOutlined />),
        getMenuItem('Agora Video', '/settings/agora', <PhoneOutlined />),
        getMenuItem('SMS Settings', '/settings/sms', <PhoneOutlined />),
        getMenuItem('Email Settings', '/settings/email', <MailOutlined />),
      ]),
    ],
    []
  );

  // Get the current full path including query params for menu matching
  const currentPath = location.pathname + location.search;

  useEffect(() => {
    if (sidebarCollapsed) {
      setOpenKeys([]);
      return;
    }

    // Find which parent menus should be open based on current path
    const pathname = location.pathname;
    const fullPath = pathname + location.search;
    const keysToOpen: string[] = [];

    // Helper to check if a key matches current path
    const isMatchingKey = (key: string): boolean => {
      if (!key) return false;
      // Exact match with full path (including query params)
      if (key === fullPath) return true;
      // Exact match with pathname only
      if (key === pathname) return true;
      // Path starts with key (for nested routes) but key must be a path
      if (key.startsWith('/') && pathname.startsWith(key) && key !== '/') return true;
      return false;
    };

    // Helper to find parent keys recursively
    const findParentKeys = (items: MenuItem[]): void => {
      items.forEach((item: any) => {
        if (!item) return;

        if (item.children) {
          // This is a submenu - check if any child matches
          const hasMatchingChild = item.children.some((child: any) => {
            if (!child) return false;
            // Check if child key matches
            if (isMatchingKey(child.key)) return true;
            // Check nested children (grandchildren)
            if (child.children) {
              return child.children.some(
                (grandChild: any) => grandChild && isMatchingKey(grandChild.key)
              );
            }
            return false;
          });

          if (hasMatchingChild) {
            keysToOpen.push(item.key);
          }

          // Also check grandchildren and add their parent keys
          item.children.forEach((child: any) => {
            if (child?.children) {
              const hasMatchingGrandChild = child.children.some(
                (gc: any) => gc && isMatchingKey(gc.key)
              );
              if (hasMatchingGrandChild) {
                if (!keysToOpen.includes(item.key)) {
                  keysToOpen.push(item.key);
                }
                keysToOpen.push(child.key);
              }
            }
          });
        }
      });
    };

    findParentKeys(menuItems);
    setOpenKeys(keysToOpen);
  }, [location.pathname, location.search, sidebarCollapsed, menuItems]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  return (
    <Sider
      className="premium-sidebar"
      collapsible
      trigger={null}
      collapsed={sidebarCollapsed}
      width={260}
      collapsedWidth={80}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
      }}
      theme="dark"
    >
      {/* Logo Section */}
      <div className="sidebar-logo">
        {sidebarCollapsed ? (
          <div className="sidebar-logo-icon">
            <MedicineBoxOutlined />
          </div>
        ) : (
          <img src={DocditLogo} alt="Docdit" style={{ height: 32 }} />
        )}
      </div>

      {/* Menu */}
      <Menu
        className="sidebar-menu"
        theme="dark"
        mode="inline"
        items={menuItems}
        selectedKeys={[currentPath]}
        openKeys={openKeys}
        onOpenChange={(keys) => setOpenKeys(keys as string[])}
        onClick={handleMenuClick}
      />
    </Sider>
  );
};

export default Sidebar;
